import { UntypedFormControl, ValidatorFn } from '@angular/forms';

export type ConnectionProfileParams = Record<string, unknown>;

export interface Connection {
  id: string;
  name: string;
  connector: string;
  description: string;
  profiles: Record<string, ConnectionProfileParams>;
  modifiedAt: Date | string;
  modifiedBy: string;
  createdAt: Date | string;
  createdBy: string;
  isSelected?: boolean; // used for UI - radio btn
  usage?: number; // in how many modules used
  attachedLabels?: string[];
}

export interface TestFlowConnectionProfile {
  profile: string;
  modules: string[];
}

/** A profile the selector renders; `available` is false for one the project no longer offers. */
export interface TestFlowConnectionProfileRow extends TestFlowConnectionProfile {
  available: boolean;
}

export interface ConnectionProfile {
  name: string;
  profile: ConnectionProfileParams;
}

export interface ConnectionProfileConfig {
  connectorName: string;
  connectorVersion: string;
}

export interface ConnectionProfiles {
  config: ConnectionProfileConfig;
  connectionRef: string;
  profiles: Record<string, ConnectionProfileParams>;
  moduleId: string;
}

export enum ConnectorOptionsPropertyType {
  STRING = 'STRING',
  INT = 'INT',
  CLASS = 'CLASS',
  ENUM = 'ENUM',
  BOOLEAN = 'BOOLEAN',
  LIST = 'LIST',
  MAP = 'MAP',
  LONG = 'LONG',
  CONSTANT = 'CONSTANT',
}

export interface ConnectorOptionsProperty {
  class: string;
  fieldName: string;
  type: ConnectorOptionsPropertyType;
  required: boolean;
  connectionParam?: boolean;
  description?: string;
  defaultValue?: string | number | boolean;
  className?: string;
  rootAlias?: string;
  elementType?: string;
  keyType?: string;
  valueType?: string;
  shortName?: string;
  includeAlways?: boolean;
  internal?: boolean;
  group?: string;
  uiType?: string;
  properties?: Record<string, ConnectorOptionsProperty>;
  enum?: string[];
}

export interface ConnectorOptionsSubGroup {
  name: string;
  priority?: number;
  description?: string;
  collapsed?: boolean;
  hidden?: boolean;
  properties: Record<string, ConnectorOptionsProperty>;
}

export interface ConnectorOptionsAdditionalParamsProperties {
  collapsed?: boolean;
  hidden?: boolean;
  name: string;
  subGroups: ConnectorOptionsSubGroup[];
}

export interface ConnectorOptionsProperties {
  name: string;
  className: string;
  descripton?: string;
  properties: ConnectorOptionsAdditionalParamsProperties;
}

export interface ConnectorConfig {
  connectionProperties: ConnectorOptionsProperties;
  additionalParams: ConnectorOptionsProperties;
  fetchProperties?: Record<string, ConnectorOptionsProperties>;
  backendSolution: string;
}

export interface ConnectorOptionsWrapper {
  connectorOptions: ConnectorConfig;
}

export interface ConnectorsOptionsWrapper {
  connectorOptionsPerModule: Record<string, ConnectorConfig>;
}

export interface ConnectorOptionsPropertyUiExtended extends Partial<ConnectorOptionsProperty> {
  label?: string;
  ngClass?:
    | string
    | string[]
    | Set<string>
    | {
        [klass: string]: unknown;
      };
  hidden?: boolean;
  validators?: ValidatorFn[];
  multiple?: boolean;
  keepInputFileArea?: boolean;
  allowedFileExtensions?: string[];
  convertInputToLowerCase?: boolean;
  convertInputToUpperCase?: boolean;
  trim?: boolean;
  convertSpecialCharsToDashes?: boolean;
  formControl?: UntypedFormControl;
  validationMessages?: Record<string, string>;
  hasMaxLengthCounter?: boolean;
  maxLength?: number;
  assetNameToPopulate?: string;
  fncToGetNameValue?: (value: Record<string, unknown>) => string;
  programPathToPopulate?: string;
  fncToGetProgramPathValue?: (value: Record<string, unknown>) => string;
}

export interface ConnectorOptionsSubGroupUiExtended
  extends Omit<Partial<ConnectorOptionsSubGroup>, 'properties'> {
  title?: string;
  shouldBeShown?: boolean;
  properties: Record<string, ConnectorOptionsPropertyUiExtended>;
  sortedProperties?: ConnectorOptionsPropertyUiExtended[];

  prepareBody?(obj: Record<string, unknown>): {
    profile: string;
    fetchRequests: Record<string, string | null | boolean | undefined>[];
  };
}

export type AdditionalParamsUiExtended = Record<
  string,
  Omit<Partial<ConnectorOptionsProperties>, 'subGroups'> & {
    subGroups: Record<string, ConnectorOptionsSubGroupUiExtended>;
  }
>;

export interface ConnectorOptionsAdditionalParamsPropertiesUiExtended
  extends Omit<ConnectorOptionsAdditionalParamsProperties, 'subGroups'> {
  subGroups: ConnectorOptionsSubGroupUiExtended[];
}

export const CONNECTION_SOLUTION_OTHER = 'other';
